import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Box, Container, Typography, Button, CircularProgress } from "@mui/material";
import VerifiedRoundedIcon from "@mui/icons-material/VerifiedRounded";
import CourseApi, { Course as CourseT } from "../api/CourseApi";
import { useAuth } from "../context/AuthContext";
import Logo from "../components/Logo";

export default function Certificate() {
  const { slug } = useParams<{ slug: string }>();
  const { user, loading } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const [course, setCourse] = useState<CourseT | null>(null);
  const [verified, setVerified] = useState(false);

  useEffect(() => { if (slug) CourseApi.get(slug).then(setCourse).catch(() => {}); }, [slug]);
  useEffect(() => {
    if (!slug || !course || !user) return;
    CourseApi.getProgress(slug).then((p) => {
      const all = course.modules.flatMap((m) => m.lessons);
      const done = all.length > 0 && all.every((l) => (p.completed || []).includes(l.key));
      if (done) setVerified(true); else navigate(`/zelfhulp/${slug}`);
    }).catch(() => navigate(`/zelfhulp/${slug}`));
  }, [slug, course, user, navigate]);

  if (loading) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;
  if (!user) {
    return (
      <Container maxWidth="sm" sx={{ py: 10, textAlign: "center" }}>
        <Typography sx={{ mb: 2 }}>{t("courses.loginToSave")}</Typography>
        <Button variant="contained" onClick={() => navigate("/inloggen")}>{t("nav.login")}</Button>
      </Container>
    );
  }
  if (!course || !verified) return <Box sx={{ minHeight: "40dvh", display: "grid", placeItems: "center" }}><CircularProgress sx={{ color: "primary.main" }} /></Box>;

  const name = (user as any).name || user.email;
  const date = new Intl.DateTimeFormat(i18n.language, { day: "numeric", month: "long", year: "numeric" }).format(new Date());

  return (
    <Box sx={{ bgcolor: "background.default", py: { xs: 4, sm: 7 } }}>
      <Container maxWidth="md">
        <Box id="certificate" sx={{ position: "relative", p: { xs: 4, sm: 8 }, textAlign: "center", borderRadius: "var(--radius-lg)", bgcolor: "#fff", border: "1px solid var(--border)", boxShadow: "var(--shadow-md)", overflow: "hidden" }}>
          <Box sx={{ position: "absolute", inset: 10, border: "2px solid #E0C893", borderRadius: "var(--radius-md)", pointerEvents: "none" }} />
          <Box sx={{ position: "relative" }}>
            <Box sx={{ display: "flex", justifyContent: "center", mb: 3 }}><Logo height={40} /></Box>
            <Typography sx={{ textTransform: "uppercase", letterSpacing: ".18em", fontSize: ".8rem", color: "secondary.dark", mb: 1 }}>{t("certificate.title")}</Typography>
            <VerifiedRoundedIcon sx={{ fontSize: 44, color: "#C2891F", mb: 1 }} />
            <Typography sx={{ color: "text.secondary", mb: 1 }}>{t("certificate.awardedTo")}</Typography>
            <Typography variant="h1" sx={{ fontFamily: "var(--font-display)", fontSize: { xs: "2rem", sm: "2.6rem" }, mb: 2 }}>{name}</Typography>
            <Typography sx={{ fontSize: "1.1rem", maxWidth: 520, mx: "auto", mb: 3 }}>
              {t("certificate.body", { course: course.title })}
            </Typography>
            <Typography variant="caption" sx={{ color: "text.secondary" }}>{t("certificate.dateLabel", { date })}</Typography>
          </Box>
        </Box>

        <Box className="no-print" sx={{ textAlign: "center", mt: 4 }}>
          <Button variant="contained" size="large" onClick={() => window.print()} sx={{ bgcolor: "primary.main", color: "#fff", "&:hover": { bgcolor: "primary.dark" } }}>
            {t("certificate.download")}
          </Button>
        </Box>
      </Container>
    </Box>
  );
}
